/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import EditIcon from '@material-ui/icons/Edit';
import { Link as ScrollLink } from 'react-scroll';
import IconButton from '@material-ui/core/IconButton';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { scrollerSettings } from '../../../../settings';
import { actionTypes, CardContext } from '../CardContext';
import Tooltip from '../../Tooltip';
import { selectVersion } from '../../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../../shared/utils/versionUtils';
import { checkTitleAndRemoveWhitespaces } from '../../../../shared/utils/stringUtils';

/**
 * Standard summary entry containing a title, value and scroll button.
 * Its content and behavior are given by the props together with the CardContext
 * of the hosting summary card.
 */
export default function Entry({ title, value, type, onClick, noScroll, customClassName, hideLinkTo }) {
  const { classes, actionType, readOnly, sectionName } = useContext(CardContext);
  const { t } = useTranslation();
  const selectedVersion = useSelector(selectVersion);

  const getXsSizes = () => {
    if (title && value && actionType === actionTypes.SUMMARY) {
      return [5, false, false];
    }
    if (title && value) {
      return [5, 7];
    }
    if (title) {
      return [12, false];
    }
    if (value) {
      return [false, 12];
    }
    return [false, false];
  };
  const [xsTitle, xsValue, xsIcon] = getXsSizes();
  const summaryCardTitle = checkTitleAndRemoveWhitespaces(title);
  const sectionNameValue = checkTitleAndRemoveWhitespaces(sectionName, 'section');
  return (
    <>
      {type !== 'listViewDetail' && (
        <Grid
          item container spacing={2} className={classes.content} justifyContent="flex-start"
          alignItems="center"
        >
          {title && (
            <Grid item xs={xsTitle}>
              <Typography
                {...dataTestId({
                  value: `${sectionNameValue}_summary_${summaryCardTitle}-title--${selectedVersion.value}`,
                })}
                variant="subtitle2"
                display="inline"
                className={classes.entryKey}
              >
                {title}
              </Typography>
            </Grid>
          )}
          {value && (
            <Grid
              item
              className={classNames(classes.entryValueGridItem, customClassName)}
              xs={xsValue}
              style={{ cursor: onClick ? 'pointer' : 'default' }}
            >
              <Grid item container spacing={2} className={classes.content}>
                {value.split('\\').map((key, index) => {
                  return (
                    <Grid
                      key={index}
                      item
                      className={classNames(classes.entryValueGridItem, customClassName)}
                      xs={12}
                      style={{ cursor: onClick ? 'pointer' : 'default' }}
                    >
                      <Tooltip
                        title={key}
                        placement="top-start"
                        arrow
                        disableHoverListener={key.length < 20}
                        hideAria={true}
                      >
                        <Typography
                          variant="body2"
                          display="inline"
                          className={classes.entryValue}
                          {...dataTestId({
                            value: `${sectionNameValue}_summary_${summaryCardTitle}_${index}-value--${selectedVersion.value}`,
                          })}
                        >
                          {!hideLinkTo && onClick ? (
                            <Link
                              id={`entry-card-link-${key}`}
                              form={`dummy-form-${uuidv4()}`}
                              href="foo"
                              component="button"
                              variant="body2"
                              onClick={onClick}
                            >
                              {key}
                            </Link>
                          ) : (
                            key
                          )}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}
          <Grid item xs={xsIcon}>
            {actionType === actionTypes.SUMMARY && !readOnly && !onClick && !noScroll && (
              <ScrollLink
                disabled={readOnly}
                activeClass="active"
                to={readOnly ? null : sectionName}
                spy={true}
                smooth={scrollerSettings.smooth}
                offset={scrollerSettings.offset}
                delay={scrollerSettings.delay}
                duration={scrollerSettings.duration}
              >
                <IconButton
                  disabled={readOnly}
                  fontSize="small"
                  scroll-link-to={readOnly ? null : sectionName}
                  aria-label={t('actions.edit')}
                >
                  <EditIcon />
                </IconButton>
              </ScrollLink>
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
}
